import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useContext, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { patchAccessory } from '@services/queries/onboarding/patchAccessory';

import { NavigationContext } from '@layout/navigation/navigation';
import { useParams } from 'react-router-dom';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Line,
  Confirm,
  Checkbox,
  CheckContainer,
  CheckText,
  WarningContainer,
  Description,
  CheckTextDescription,
  CheckDivider,
  TextsRowFlex,
  TextsRowFlexItem,
  TextTitle,
  TextDescription,
  ToolTipText,
  Cancel,
} from './AccessoryDialog.styles';
import Labels from '../Labels/Labels';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { ErrorsFoundDialog } from '../ErrorsFoundDialog/ErrorsFoundDialog';

interface IAccessoryDialog {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
  tab: string;
}

export const AccessoryDialog = ({
  open,
  onClose,
  group,
  tab,
}: IAccessoryDialog) => {
  const portalDiv = document.getElementById('portal');

  const params = useParams<{ id: string }>();

  const { studioId, userEmail, userName } = useContext(NavigationContext);

  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<string | null>(null);
  const [openDiscard, setOpenDiscard] = useState<boolean>(false);
  const [openIdentification, setOpenIdentification] = useState<boolean>(false);
  const [openInternal, setOpenInternal] = useState<boolean>(false);
  const [openHasError, setOpenHasError] = useState<boolean>(false);

  const description = useMemo(() => {
    if (group.isManufacturerAccessory) {
      return `${group.manufacturer} products are usually accessories or related. How would you like to proceed:`;
    }

    if (group.accessoryKeyword.length > 0) {
      return `Products named with the word “${group.accessoryKeyword[0]}” are usually accessories or
      related. How would you like to proceed:`;
    }

    return 'This product seems to be an Internal Product. How would you like to proceed:';
  }, [group]);

  const originalInput = useMemo(() => {
    if (group.accessoryKeyword.length > 0) {
      const indexKeywordModel = group.model
        .toLowerCase()
        .indexOf(group.accessoryKeyword[0]);
      const indexKeywordProductName = group.productName
        .toLowerCase()
        .indexOf(group.accessoryKeyword[0]);

      if (indexKeywordModel !== -1) {
        return group.model;
      }

      if (indexKeywordProductName !== -1) {
        return group.productName;
      }
    }

    return group.manufacturer;
  }, [group]);

  const accessoryMutation = useMutation(patchAccessory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      onClose();
    },
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <WarningContainer>
                <BlueIcon icon="warning-sign" size={16} color="#FF9900" />
              </WarningContainer>
              <Title>Possible Accessory</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <TextsRowFlex>
              <TextsRowFlexItem>
                <TextTitle>Original Input</TextTitle>
                <TextDescription>{originalInput}</TextDescription>
              </TextsRowFlexItem>

              <TextsRowFlexItem>
                <Labels
                  text="Internal"
                  toolTipId="intenalProductTooltip"
                  type="warning"
                  toolTipContent={
                    <>
                      <ToolTipText>
                        This product seems to be an Accessory.
                      </ToolTipText>
                    </>
                  }
                />
              </TextsRowFlexItem>
            </TextsRowFlex>

            <Description>{description}</Description>

            {tab !== 'inventory' && (
              <>
                <CheckContainer onClick={() => setSelected('inventory')}>
                  <Checkbox active={selected === 'inventory'} />
                  <CheckText>Add as Internal Product</CheckText>
                </CheckContainer>
                <CheckTextDescription>
                  This type of product is added immediately, but no additional
                  product data can be included by Beam. However, you’ll be able
                  to add your own information.
                </CheckTextDescription>
              </>
            )}

            <CheckDivider />

            <CheckContainer
              onClick={() => setSelected('identification_requested')}
            >
              <Checkbox active={selected === 'identification_requested'} />
              <CheckText>Identify Product</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              We will remove the Internal flag and this product will return to
              the list as a common product to be processed just like the others.
              If there are no more errors, this product will be moved directly
              to the <b>Identified</b> tab.
            </CheckTextDescription>
            <CheckDivider />

            {tab !== 'inventory' && (
              <>
                <CheckContainer onClick={() => setSelected('discarded')}>
                  <Checkbox active={selected === 'discarded'} />
                  <CheckText>Discard it</CheckText>
                </CheckContainer>
                <CheckTextDescription>
                  In case you don’t need this product, you can <b>discard</b> it
                  now.
                  <p>
                    <i>
                      It’ll appear in the “Discarded” tab. You can always “undo”
                      if necessary.
                    </i>
                  </p>
                </CheckTextDescription>
              </>
            )}
            <Line>
              <Cancel type="button" onClick={onClose}>
                Cancel
              </Cancel>
              <Confirm
                type="button"
                disabled={!selected || accessoryMutation.isLoading}
                onClick={() => {
                  if (selected === 'inventory') {
                    if (group.mfrError) {
                      setOpenHasError(true);
                    } else {
                      setOpenInternal(true);
                    }
                  }
                  if (selected === 'discarded') {
                    setOpenDiscard(true);
                  }
                  if (selected === 'identification_requested') {
                    setOpenIdentification(true);
                  }
                }}
              >
                Confirm
              </Confirm>
            </Line>
          </Container>
          {openInternal && (
            <ConfirmDialog
              open={openInternal}
              onClose={() => setOpenInternal(false)}
              isLoading={accessoryMutation.isLoading}
              onConfirm={() => {
                if (selected) {
                  accessoryMutation.mutate({
                    groupId: group.id,
                    status: selected,
                    studioId,
                    fileId: params.id,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              showAsset
              isLarge
              group={group}
              title="Internal Product added"
              description="This type of product is added immediatelly, but no additional product data can be included by Beam. However, you’ll be able to add your own information."
            />
          )}
          {openIdentification && (
            <ConfirmDialog
              open={openIdentification}
              onClose={() => setOpenIdentification(false)}
              isLoading={accessoryMutation.isLoading}
              onConfirm={() => {
                if (selected) {
                  accessoryMutation.mutate({
                    groupId: group.id,
                    status: selected,
                    studioId,
                    fileId: params.id,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              title="Identification in process"
              description="The product information is being processed. If there are errors, you will see them in the “Unidentified” section. Otherwise, it will be moved to the “Identified” section."
            />
          )}
          {openDiscard && (
            <ConfirmDialog
              open={openDiscard}
              onClose={() => setOpenDiscard(false)}
              isLoading={accessoryMutation.isLoading}
              onConfirm={() => {
                if (selected) {
                  accessoryMutation.mutate({
                    groupId: group.id,
                    status: selected,
                    studioId,
                    fileId: params.id,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              title="Product Discarded"
              description="The product was discarded and can be found in the “Discarded” tab."
            />
          )}
          {openHasError && (
            <ErrorsFoundDialog
              open={openHasError}
              onClose={() => setOpenHasError(false)}
              group={group}
              onSkip={() => {
                accessoryMutation.mutate({
                  groupId: group.id,
                  status: 'inventory',
                  studioId,
                  fileId: params.id,
                  triggeredBy: userName,
                  triggeredByEmail: userEmail,
                });
              }}
              isLoading={accessoryMutation.isLoading}
              closeFlow={onClose}
            />
          )}
        </Overlay>,
        portalDiv,
      )
    : null;
};
